import React, { useState, useEffect } from 'react';
import axios from 'axios';
const api = axios.create({ baseURL: '/api' });
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

function NaverMatchCell({ categoryId, current, currentName, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [keyword, setKeyword] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [searching, setSearching] = useState(false);
  const [saving, setSaving] = useState(false);

  const runSearch = async (kw) => {
    setKeyword(kw);
    if (!kw.trim()) { setCandidates([]); return; }
    setSearching(true);
    try {
      const r = await api.get('/product-settings/naver/categories/search', { params: { q: kw.trim() } });
      setCandidates(r.data || []);
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  };

  const save = async (c) => {
    setSaving(true);
    try {
      await api.post('/categories/manage/naver-mapping', {
        category_id: categoryId,
        platform_category_no: c.id,
        platform_category_name: c.whole_name,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      alert('저장 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span onClick={() => setEditing(true)} style={{ cursor: 'pointer', fontSize: '13px', color: current ? '#185FA5' : '#aaa', borderBottom: '1px dashed #ccc' }}>
        {current ? `${currentName || ''} (${current})` : '매칭 안 됨 (클릭)'}
      </span>
    );
  }
  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <input autoFocus value={keyword} onChange={e => runSearch(e.target.value)}
        placeholder="네이버 카테고리 검색"
        onKeyDown={e => { if (e.key === 'Escape') setEditing(false); }}
        style={{ fontSize: '12px', padding: '4px 6px', borderRadius: '4px', border: '0.5px solid #1D9E75', width: '200px' }} />
      {(candidates.length > 0 || searching) && (
        <div style={{ position: 'absolute', top: '28px', left: 0, zIndex: 10, background: '#fff', border: '0.5px solid #ddd', borderRadius: '6px', width: '280px', maxHeight: '220px', overflowY: 'auto', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          {searching && <div style={{ padding: '8px', fontSize: '11px', color: '#aaa' }}>검색 중...</div>}
          {candidates.map((c, i) => (
            <div key={i} onClick={() => !saving && save(c)}
              style={{ padding: '6px 10px', fontSize: '11px', cursor: saving ? 'not-allowed' : 'pointer', borderBottom: '0.5px solid #f0f0f0' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f5f5f5'}
              onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
              {c.whole_name}
            </div>
          ))}
        </div>
      )}
      <button onClick={() => setEditing(false)} style={{ fontSize: '10px', color: '#aaa', marginLeft: '4px', border: 'none', background: 'none', cursor: 'pointer' }}>취소</button>
    </div>
  );
}

// 신규 카테고리 추가 팝업
function AddCategoryModal({ onClose, onAdded }) {
  const [keyword, setKeyword] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState(null);

  const runSearch = async (kw) => {
    setKeyword(kw);
    if (!kw.trim()) { setResults([]); return; }
    setSearching(true);
    try {
      const r = await api.get('/categories/manage/naver-search-new', { params: { q: kw.trim() } });
      setResults(r.data || []);
    } catch (err) { console.error(err); }
    finally { setSearching(false); }
  };

  const addCategory = async (item) => {
    setAddingId(item.id);
    try {
      await api.post('/categories/manage/add-new', { naver_id: item.id, whole_name: item.whole_name });
      setResults(prev => prev.map(r => r.id === item.id ? { ...r, alreadyExists: true } : r));
      onAdded();
    } catch (err) {
      alert('추가 실패: ' + (err.response?.data?.message || err.message));
    } finally {
      setAddingId(null);
    }
  };

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '520px', maxHeight: '70vh', display: 'flex', flexDirection: 'column', boxShadow: '0 4px 24px rgba(0,0,0,0.15)' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', marginBottom: '12px', color: '#1a1a1a' }}>신규 카테고리 추가</div>
        <input autoFocus value={keyword} onChange={e => runSearch(e.target.value)}
          placeholder="네이버 카테고리 검색 (예: 마시멜로)"
          style={{ fontSize: '13px', padding: '8px 12px', borderRadius: '8px', border: '0.5px solid #ddd', marginBottom: '12px' }} />
        <div style={{ flex: 1, overflowY: 'auto', border: '0.5px solid #e8e8e8', borderRadius: '8px', minHeight: '80px' }}>
          {searching && <div style={{ padding: '14px', fontSize: '12px', color: '#aaa' }}>검색 중...</div>}
          {!searching && results.length === 0 && keyword && (
            <div style={{ padding: '14px', fontSize: '12px', color: '#aaa', textAlign: 'center' }}>검색 결과가 없습니다</div>
          )}
          {results.map((r, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '9px 12px', borderBottom: '0.5px solid #f0f0f0' }}>
              <span style={{ fontSize: '12px', color: r.alreadyExists ? '#aaa' : '#1a1a1a' }}>{r.whole_name}</span>
              {r.alreadyExists ? (
                <span style={{ fontSize: '11px', color: '#aaa' }}>이미 있음</span>
              ) : (
                <button onClick={() => addCategory(r)} disabled={addingId === r.id}
                  style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '6px', border: '0.5px solid #1D9E75', background: addingId === r.id ? '#f0f0f0' : '#E1F5EE', color: '#1D9E75', cursor: addingId === r.id ? 'not-allowed' : 'pointer' }}>
                  {addingId === r.id ? '추가 중...' : '추가'}
                </button>
              )}
            </div>
          ))}
        </div>
        <button onClick={onClose} style={{ fontSize: '13px', padding: '10px', borderRadius: '8px', border: 'none', background: '#3C3489', color: '#fff', cursor: 'pointer', fontWeight: '500', marginTop: '14px' }}>
          닫기
        </button>
      </div>
    </div>
  );
}

export default function CategoryManage() {
  const [larges, setLarges] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedLarge, setSelectedLarge] = useState(null);
  const [middles, setMiddles] = useState([]);
  const [selectedMiddle, setSelectedMiddle] = useState(null);
  const [smalls, setSmalls] = useState([]);
  const [selectedSmall, setSelectedSmall] = useState(null);
  const [details, setDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get('/categories/manage/tree', { params: { level: 'large' } }).then(r => setLarges(r.data || []));
    // [기능 추가 - 2026-09-20] 대분류>중분류>소분류를 일일이 눌러야만 매칭 여부를
    // 알 수 있어, 미매칭 카테고리 때문에 상품 등록이 실패하는 걸 뒤늦게 발견하는
    // 문제가 있었다. 전체/미매칭 개수를 화면 상단에 바로 보여준다.
    api.get('/categories/manage/stats').then(r => setStats(r.data));
  }, []);

  const openLarge = async (large) => {
    setSelectedLarge(large);
    setSelectedMiddle(null); setSelectedSmall(null);
    setSmalls([]); setDetails([]);
    const r = await api.get('/categories/manage/tree', { params: { level: 'middle', large } });
    setMiddles(r.data || []);
  };
  const openMiddle = async (middle) => {
    setSelectedMiddle(middle);
    setSelectedSmall(null); setDetails([]);
    const r = await api.get('/categories/manage/tree', { params: { level: 'small', large: selectedLarge, middle } });
    setSmalls(r.data || []);
  };
  const openSmall = async (small) => {
    setSelectedSmall(small);
    setLoading(true);
    try {
      const r = await api.get('/categories/manage/tree', { params: { level: 'detail', large: selectedLarge, middle: selectedMiddle, small } });
      setDetails(r.data || []);
    } finally { setLoading(false); }
  };
  const refreshDetails = () => {
    if (selectedSmall) openSmall(selectedSmall);
    api.get('/categories/manage/stats').then(r => setStats(r.data));
  };

  const colStyle = { width: '150px', borderRight: '0.5px solid #e8e8e8', maxHeight: '650px', overflowY: 'auto', flexShrink: 0 };
  const itemStyle = (active) => ({
    padding: '9px 12px', fontSize: '13px', cursor: 'pointer', color: active ? '#1D9E75' : '#1a1a1a',
    background: active ? '#E1F5EE' : 'transparent', fontWeight: active ? '600' : '400', borderBottom: '0.5px solid #f5f5f5',
  });
  const rowStyle = { padding: '9px 12px', fontSize: '13px', borderBottom: '0.5px solid #f5f5f5' };

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>카테고리 관리</h2>
        <button onClick={() => setShowAddModal(true)} style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '8px', border: '0.5px solid #ddd', background: '#fff', cursor: 'pointer', fontWeight: '500' }}>
          + 신규 카테고리 추가
        </button>
      </div>
      {showAddModal && (
        <AddCategoryModal onClose={() => setShowAddModal(false)} onAdded={() => { api.get('/categories/manage/tree', { params: { level: 'large' } }).then(r => setLarges(r.data || [])); }} />
      )}
      <div style={{ fontSize: '12px', color: '#888', marginBottom: '16px', lineHeight: '1.6' }}>
        우리 카테고리와 스마트스토어(네이버) 카테고리를 매칭합니다. 대분류 → 중분류 → 소분류 순서로 선택하세요.<br/>
        패션잡화 등 세분류가 여러 개로 나뉘는 대분류는, 실제 상품 등록 시 정확한 세분류를 확인 후 매칭해주세요.
      </div>
      {stats && (
        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
          <div style={{ padding: '8px 14px', borderRadius: '8px', background: '#f5f5f5', fontSize: '13px' }}>
            전체 <b>{stats.total}</b>개
          </div>
          <div style={{ padding: '8px 14px', borderRadius: '8px', background: stats.unmatched > 0 ? '#FCEBEB' : '#E1F5EE', color: stats.unmatched > 0 ? '#A32D2D' : '#1D9E75', fontSize: '13px', fontWeight: '500' }}>
            미매칭 <b>{stats.unmatched}</b>개
          </div>
        </div>
      )}
      <div style={{ display: 'flex', marginBottom: '2px' }}>
        <div style={{ width: '610px', padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: '#185FA5', background: '#E6F1FB', borderRadius: '8px 8px 0 0' }}>자사 카테고리</div>
        <div style={{ flex: 1, padding: '7px 12px', fontSize: '12px', fontWeight: '600', color: '#1D9E75', background: '#E1F5EE', marginLeft: '4px', borderRadius: '8px 8px 0 0' }}>스마트스토어 카테고리</div>
      </div>
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: '0 12px 12px 12px', display: 'flex', overflow: 'hidden' }}>
        <div style={colStyle}>
          {larges.map(l => (
            <div key={l} style={itemStyle(l === selectedLarge)} onClick={() => openLarge(l)}>{l}</div>
          ))}
        </div>
        <div style={colStyle}>
          {middles.map(m => (
            <div key={m} style={itemStyle(m === selectedMiddle)} onClick={() => openMiddle(m)}>{m}</div>
          ))}
        </div>
        <div style={colStyle}>
          {smalls.map(s => (
            <div key={s} style={itemStyle(s === selectedSmall)} onClick={() => openSmall(s)}>{s}</div>
          ))}
        </div>
        <div style={{ width: '160px', borderRight: '0.5px solid #e8e8e8', maxHeight: '650px', overflowY: 'auto', flexShrink: 0 }}>
          {loading ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>불러오는 중...</div>
          ) : details.length === 0 ? (
            <div style={{ padding: '20px', textAlign: 'center', color: '#aaa', fontSize: '12px' }}>{selectedSmall ? '항목 없음' : '소분류 선택'}</div>
          ) : (
            details.map(d => (
              <div key={d.id} style={rowStyle}>{d.detail_category || '(세분류 없음)'}</div>
            ))
          )}
        </div>
        <div style={{ flex: 1, maxHeight: '650px', overflowY: 'auto' }}>
          {!loading && details.map(d => (
            <div key={d.id} style={rowStyle}>
              <NaverMatchCell categoryId={d.id} current={d.platform_category_no} currentName={d.platform_category_name} onSaved={refreshDetails} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
